<template>
  <div :id="`${source}_call_show_details`" class="w-full">
    <SkeletonLoader
      v-if="isLoading"
      :rsk-size="40"
      :page="loaderPages.DIALER"
    />

    <div v-else class="flex flex-col items-center gap-2 p-2">
      <ProfileIcon :data="primaryDisplayName" :size="40" :font="16" />

      <div
        v-if="statusValue"
        class="flex items-center gap-1 py-[3px] pl-1 pr-2 rounded-full text-[11px] font-semibold grey_tag"
      >
        <ThemeImage class="size-[13px]" :icon="imageNames.WHITE_DOT" alt="white_dot" />
        <span>{{ statusValue }}</span>
      </div>

      <div class="flex flex-col gap-2 items-center">
        <div class="flex flex-col items-center">
          <span class="text-xl font-semibold text_color">{{ primaryDisplayName }}</span>
          <span v-if="!showAddToLeadOrContact" class="text-sm">{{
            phoneNumber
          }}</span>
        </div>

        <div class="flex flex-col">
          <AddToLeadOrContact
            v-if="showAddToLeadOrContact"
            source="call_dialog_details"
            :number="phoneNumber"
            @update:added="handleNumberAdded"
          />

          <span
            v-else-if="primaryDetails?.id"
            class="text-xs secondary_color cursor-pointer"
            @click.stop="handleShowDetails"
            @mousedown.stop
            @touchstart.stop
          >
            View Profile Details
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { renderEntity } from "@/src/utils/utils.js";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import { conferenceParticipantLabels } from "@/src/constants/callConstants.js";
import ProfileIcon from "@/src/components/ProfileIcon.vue";

import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import AddToLeadOrContact from "@/src/components/AddToLeadOrContact.vue";
import {imageNames} from "@/src/constants/imageConstants.js";

const props = defineProps({
  source: { type: String, required: true },
  contactDetails: { type: Object, required: true },
  callDetails: { type: Object, required: true },
  isIncoming: { type: Boolean, default: false },
});

const store = useStore();
const router = useRouter();
const updatedDetails = ref(null);

const participants = computed(
  () => store.getters["conferenceCall/participants"],
);

const isLoading = computed(
  () =>
    store.getters["conferenceCall/transferringCall"] ||
    store.getters["conferenceCall/swappingCalls"] ||
    store.getters["calls/generatingLead"] ||
    store.getters["calls/fetchingDetails"],
);

const myCallSid = computed(() => store.getters["conferenceCall/myCallSid"]);

const conferenceActiveParticipant = computed(() => {
  const participant = participants.value?.find(
    (p) =>
      (!myCallSid.value || p.call_sid !== myCallSid.value) &&
      !p.hold &&
      (p.label === conferenceParticipantLabels.CALLER ||
        p.label === conferenceParticipantLabels.TRANSFER_TARGET ||
        p.label === conferenceParticipantLabels.ORIGINAL_AGENT),
  );

  if (participant) {
    const { entity_id, entity_type, ...rest } = participant;
    return {
      ...rest,
      id: entity_id,
      type: entity_type,
    };
  }

  return null;
});

const primaryDisplayName = computed(() => {
  return (
    renderEntity(conferenceActiveParticipant.value) ||
    renderEntity(updatedDetails.value) ||
    renderEntity(props.contactDetails) ||
    renderEntity(props.callDetails)
  );
});

const phoneNumber = computed(() => {
  return (
    conferenceActiveParticipant.value?.phone_number ||
    updatedDetails.value?.phone_number ||
    props.callDetails?.number ||
    props.contactDetails?.phone_number
  );
});

const primaryDetails = computed(() => {
  return (
    conferenceActiveParticipant.value ||
    updatedDetails.value ||
    props.contactDetails
  );
});

const showAddToLeadOrContact = computed(() => {
  return !props.isIncoming && !props.contactDetails && !updatedDetails.value;
});

const entityDetails = computed(() => store.getters["calls/entityDetails"]);
const statusValue = computed(() => {
  if (!entityDetails.value) return null;
  return (
    entityDetails.value.lead_status ||
    entityDetails.value.contact_status ||
    null
  );
});

const handleShowDetails = () => {
  try {
    const entityType = primaryDetails.value?.type || primaryDetails.value?.entity_type;
    router.push({
      name: `${entityType}-detail`,
      params: { id: primaryDetails.value?.id },
    });
  } catch (error) {
    console.error("An error occurred while handling show details: ", error);
  }
};

const handleNumberAdded = async (value) => {
  try {
    updatedDetails.value = value;
  } catch (error) {
    console.error("An error occurred while handling number added: ", error);
  }
};
</script>
